import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { Link } from 'react-router-dom';

import './stylesheet.css';

import * as baseActions from '../../actions/base';

import { parseOrg } from '../../lib/parse_org';
import { dashboardConfigFromState, filePathToUrl } from '../../lib/dashboard';
import {
  collectTasks,
  currentRun,
  dailyFoldersForWindow,
  dailyPathsForWindow,
  suggestedWeeklyTarget,
  summarizeWeek,
  windowOfDays,
  WEEK_WINDOW_DAYS,
} from '../../lib/week_dashboard';

// Enough to see the shape of a day without the column becoming a wall of dots.
const MAX_DOTS_PER_DAY = 6;
// Working memory holds about four chunks (Cowan 2001); five is the concept's ceiling.
const FOCUS_SET_SIZE = 5;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const dayLabel = (date) => `${WEEKDAYS[date.getDay()]} ${date.getDate()}`;

const TaskRow = ({ task }) => (
  <Link className="week__task" to={filePathToUrl(task.path)}>
    <span className={`week__task__marker week__task__marker--${task.kind}`}>
      {task.kind === 'headline' ? task.todoKeyword : task.checkboxState === 'partial' ? '[-]' : '[ ]'}
    </span>
    <span className="week__task__text">{task.text || '(untitled)'}</span>
    {task.hasPlanning && <i className="fas fa-calendar-check week__task__planned" title="Planned" />}
  </Link>
);

const WeekDashboard = ({
  base,
  syncBackendClient,
  storedConfig,
  weeklyTaskTarget,
  isOnline,
}) => {
  const config = useMemo(() => dashboardConfigFromState(storedConfig), [storedConfig]);

  const today = useMemo(() => new Date(), []);
  const days = useMemo(() => windowOfDays(today, WEEK_WINDOW_DAYS), [today]);

  const [files, setFiles] = useState(null);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [isEditingTarget, setIsEditingTarget] = useState(false);
  const [showCarryOver, setShowCarryOver] = useState(false);

  const load = useCallback(async () => {
    if (!syncBackendClient) {
      setError('Not connected to a sync backend.');
      return;
    }

    setError(null);
    setFiles(null);

    const expected = dailyPathsForWindow(config.dailyPathTemplate, today, WEEK_WINDOW_DAYS);

    try {
      // Listed through the client rather than the getDirectoryListing action, so
      // opening this page does not disturb the directory the file browser is showing.
      // Knowing which notes exist avoids a failed fetch per day off.
      const folders = dailyFoldersForWindow(config.dailyPathTemplate, today, WEEK_WINDOW_DAYS);
      const listings = await Promise.all(
        folders.map((folder) =>
          syncBackendClient.getDirectoryListing(folder).catch(() => null)
        )
      );

      const present = new Set();
      listings.forEach((listing) => {
        if (!listing) return;
        (listing.listing || []).forEach((file) => {
          const path = file.get ? file.get('path') : file.path;
          if (path) present.add(path);
        });
      });

      const wanted = [
        ...expected
          .filter(({ path }) => present.has(path))
          .map(({ path, date }) => ({ path, date })),
        // gtd.org is always read: it is the standing list, not a day.
        ...(config.gtdPath ? [{ path: config.gtdPath, date: null }] : []),
      ];

      const collected = await Promise.all(
        wanted.map(async ({ path, date }) => {
          try {
            const contents = await syncBackendClient.getFileContents(path);
            const parsed = parseOrg(contents);

            return {
              path,
              date,
              ...collectTasks({
                headers: parsed.get('headers'),
                todoKeywordSets: parsed.get('todoKeywordSets'),
                path,
                date,
              }),
            };
          } catch (fileError) {
            // One unreadable file must not blank the whole page.
            console.warn(`Could not read ${path}:`, fileError);
            return null;
          }
        })
      );

      setFiles(collected.filter(Boolean));
    } catch (listingError) {
      console.error('Could not build the week dashboard:', listingError);
      setError(listingError.message);
      setFiles([]);
    }
  }, [config.dailyPathTemplate, config.gtdPath, syncBackendClient, today]);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  const summary = useMemo(() => summarizeWeek({ files: files || [], days }), [files, days]);

  const target = weeklyTaskTarget || suggestedWeeklyTarget(summary.doneCount);
  const toGo = Math.max(0, target - summary.doneCount);
  const progressPercent = target > 0 ? Math.min(100, Math.round((summary.doneCount / target) * 100)) : 0;
  const run = currentRun(summary.dayColumns);

  const dailyFiles = (files || []).filter((file) => !!file.date);
  const gtdFile = (files || []).find((file) => !file.date);

  // A transparent rule, so the focus set never looks arbitrary: already-planned items
  // first, then the standing list, then the freshest days.
  const focusSet = useMemo(() => {
    const planned = [];
    const fromGtd = [];
    const fromDays = [];

    (files || []).forEach((file) => {
      file.open.forEach((task) => {
        if (task.hasPlanning) planned.push(task);
        else if (!file.date) fromGtd.push(task);
        else fromDays.push(task);
      });
    });

    // Newest day first among the dailies.
    fromDays.sort((a, b) => (b.date ? b.date.getTime() : 0) - (a.date ? a.date.getTime() : 0));

    return [...planned, ...fromGtd, ...fromDays].slice(0, FOCUS_SET_SIZE);
  }, [files]);

  const handleTargetChange = (event) => {
    const parsed = parseInt(event.target.value, 10);
    if (!isNaN(parsed) && parsed > 0) {
      base.setWeeklyTaskTarget(parsed);
    }
  };

  if (files === null) {
    return (
      <div className="week-container">
        <div className="week__header">
          <h3 className="week__title">This week</h3>
          <Link className="week__header__action" to="/dashboard">
            <i className="fas fa-th-large" /> Dashboard
          </Link>
        </div>
        <div className="week__loading">
          <i className="fas fa-spinner fa-spin" /> Reading the last {WEEK_WINDOW_DAYS} days...
        </div>
      </div>
    );
  }

  return (
    <div className="week-container">
      <div className="week__header">
        <h3 className="week__title">This week</h3>
        <div className="week__header__actions">
          <button className="week__header__action" onClick={() => setReloadToken((n) => n + 1)}>
            <i className="fas fa-sync-alt" /> Refresh
          </button>
          <Link className="week__header__action" to="/dashboard/contract">
            <i className="fas fa-file-signature" /> Contract
          </Link>
          <Link className="week__header__action" to="/dashboard/focus">
            <i className="fas fa-bullseye" /> Focus
          </Link>
          <Link className="week__header__action" to="/dashboard/team">
            <i className="fas fa-users" /> Team
          </Link>
          <Link className="week__header__action" to="/dashboard/formation">
            <i className="fas fa-seedling" /> Formation
          </Link>
          <Link className="week__header__action" to="/dashboard/review">
            <i className="fas fa-moon" /> Review
          </Link>
          <Link className="week__header__action" to="/dashboard">
            <i className="fas fa-th-large" /> Dashboard
          </Link>
        </div>
      </div>

      {!!error && <div className="week__error">{error}</div>}
      {!isOnline && <div className="week__error">Offline — showing what could be read.</div>}

      {/* Momentum: progress first, backlog second. */}
      <div className="week__momentum">
        <div className="week__bar" role="progressbar" aria-valuenow={summary.doneCount} aria-valuemax={target}>
          <div className="week__bar__fill" style={{ width: `${progressPercent}%` }} />
        </div>

        <div className="week__momentum__line">
          <strong>
            {summary.doneCount} / {target} done
          </strong>
          {isEditingTarget ? (
            <input
              className="textfield week__target__input"
              type="number"
              min="1"
              autoFocus
              defaultValue={target}
              onChange={handleTargetChange}
              onBlur={() => setIsEditingTarget(false)}
            />
          ) : (
            <button className="week__target__button" onClick={() => setIsEditingTarget(true)}>
              target ▾
            </button>
          )}
        </div>

        <div className="week__momentum__detail">
          {toGo} to go · {summary.openCount} open · {summary.daysWithNotes} of {WEEK_WINDOW_DAYS}{' '}
          days noted
          {run > 0 && ` · run of ${run} day${run === 1 ? '' : 's'}`}
        </div>
      </div>

      {/* The seven-day strip. A day with no note is '·', never a zero. */}
      <div className="week__strip">
        {summary.dayColumns.map((column) => (
          <div
            className={`week__day${column.hasNote ? '' : ' week__day--no-note'}`}
            key={column.date.toDateString()}
          >
            <div className="week__day__label">{dayLabel(column.date)}</div>
            <div className="week__day__dots">
              {column.hasNote ? (
                <>
                  {Array.from({ length: Math.min(column.doneCount, MAX_DOTS_PER_DAY) }).map(
                    (_unused, index) => (
                      <span className="week__dot week__dot--done" key={`d${index}`} />
                    )
                  )}
                  {Array.from({
                    length: Math.min(
                      column.openCount,
                      Math.max(0, MAX_DOTS_PER_DAY - column.doneCount)
                    ),
                  }).map((_unused, index) => (
                    <span className="week__dot week__dot--open" key={`o${index}`} />
                  ))}
                </>
              ) : (
                <span className="week__day__none">·</span>
              )}
            </div>
            <div className="week__day__counts">
              {column.hasNote ? `${column.doneCount}/${column.doneCount + column.openCount}` : '—'}
            </div>
          </div>
        ))}
      </div>

      {focusSet.length > 0 && (
        <>
          <div className="week__section-title">Now</div>
          <div className="week__tasks">
            {focusSet.map((task) => (
              <TaskRow task={task} key={task.key} />
            ))}
          </div>
        </>
      )}

      {!!gtdFile && gtdFile.open.length > 0 && (
        <>
          <div className="week__section-title">
            <span>{gtdFile.path}</span>
            <span className="week__section-title__count">{gtdFile.open.length} open</span>
          </div>
          <div className="week__tasks">
            {gtdFile.open.map((task) => (
              <TaskRow task={task} key={task.key} />
            ))}
          </div>
        </>
      )}

      {/* Older days collapsed: an always-visible unbounded backlog demotivates. */}
      {dailyFiles.length > 0 && (
        <>
          <div className="week__section-title">
            <span>Daily notes</span>
            <button className="week__section-title__action" onClick={() => setShowCarryOver((v) => !v)}>
              {showCarryOver ? 'Hide' : `Show ${dailyFiles.reduce((n, f) => n + f.open.length, 0)} open`}
            </button>
          </div>

          {showCarryOver &&
            dailyFiles
              .slice()
              .sort((a, b) => b.date.getTime() - a.date.getTime())
              .filter((file) => file.open.length > 0)
              .map((file) => (
                <div className="week__day-group" key={file.path}>
                  <div className="week__day-group__title">{dayLabel(file.date)}</div>
                  <div className="week__tasks">
                    {file.open.map((task) => (
                      <TaskRow task={task} key={task.key} />
                    ))}
                  </div>
                </div>
              ))}
        </>
      )}

      {summary.openCount === 0 && !error && (
        <div className="week__empty">Nothing open in the last {WEEK_WINDOW_DAYS} days. </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  syncBackendClient: state.syncBackend.get('client'),
  storedConfig: state.base.get('dashboardConfig'),
  weeklyTaskTarget: state.base.get('weeklyTaskTarget'),
  isOnline: state.base.get('online'),
});

const mapDispatchToProps = (dispatch) => ({
  base: bindActionCreators(baseActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(WeekDashboard);
